import { createRoot, type Root } from "react-dom/client";
import { createElement } from "react";
import type {
  ConfiguratorConfig,
  RendererKind,
} from "@/configurator/domain/models/types";
import { configuratorStateAdapter } from "@/configurator/data/store/configuratorStateAdapter";
import { ConfiguratorStateProvider } from "@/configurator/domain/hooks/useConfiguratorStateDomain";
import { WidgetRoot } from "@/widget/WidgetRoot";

type DefineOptions = {
  tagName?: string;
  defaultAssetsBaseUrl?: string;
  stylesText?: string;
  fontFaceText?: string;
};

const FONT_STYLE_ID = "sk-configurator-fonts";

/**
 * Injects @font-face rules into the host document once.
 *
 * They cannot live in the Shadow DOM stylesheet with the rest of our CSS: font
 * faces resolve only against the document's font face set, so `@font-face`
 * inside a shadow root is ignored and the family silently falls back.
 */
function ensureDocumentFontFaces(fontFaceText: string) {
  if (!fontFaceText) return;
  if (document.getElementById(FONT_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = FONT_STYLE_ID;
  style.textContent = fontFaceText;
  document.head.appendChild(style);
}

function innerWidth(element: Element): number {
  const styles = getComputedStyle(element);
  return (
    element.clientWidth -
    (parseFloat(styles.paddingLeft) || 0) -
    (parseFloat(styles.paddingRight) || 0)
  );
}

/**
 * Finds the width the host page actually has room for, when the element's own
 * parent refuses to give it one.
 *
 * `:host { width: 100% }` only works if some ancestor has a definite width.
 * Page builders love to wrap embeds in shrink-to-fit boxes — an Oxygen/WP code
 * block inside a centered flex column, for example — and those size themselves
 * from the widget's max-content width instead. The widget then renders at the
 * intrinsic width of its own options card (~350px) no matter how wide the
 * section around it is, which is not a layout anybody asked for.
 *
 * So collapse the element to zero width first. Every shrink-to-fit ancestor
 * collapses with it; the first one that keeps a width is the first one the page
 * layout sizes, and that is the width to adopt. Collapsing rather than comparing
 * against our current width keeps the answer independent of whether React has
 * painted yet, and of how wide the content happens to be.
 */
function findAvailableWidth(element: HTMLElement): {
  source: HTMLElement;
  width: number;
} | null {
  const previousWidth = element.style.width;
  element.style.width = "0px";

  try {
    let ancestor = element.parentElement;
    while (ancestor && ancestor !== document.body) {
      // 1px of slack: sub-pixel layout rounding is not a real width.
      const available = innerWidth(ancestor);
      if (available > 1) return { source: ancestor, width: available };
      ancestor = ancestor.parentElement;
    }
    return null;
  } finally {
    element.style.width = previousWidth;
  }
}

function parseJsonAttribute<T>(value: string | null): T | undefined {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

export function defineConfiguratorElement(options: DefineOptions = {}) {
  const tagName = options.tagName ?? "sk-configurator";
  if (customElements.get(tagName)) return;

  class SkConfiguratorElement extends HTMLElement {
    static observedAttributes = [
      "data-config-id",
      "data-config",
      "data-renderer",
      "data-assets-base-url",
    ];

    private _root: Root | null = null;
    private _shadowRoot: ShadowRoot | null = null;
    private _mountNode: HTMLDivElement | null = null;
    private _widthObserver: ResizeObserver | null = null;

    connectedCallback() {
      if (!this._root) {
        this._shadowRoot =
          this.shadowRoot ?? this.attachShadow({ mode: "open" });

        ensureDocumentFontFaces(options.fontFaceText ?? "");

        const style = document.createElement("style");
        style.textContent = options.stylesText ?? "";
        this._shadowRoot.appendChild(style);

        this._mountNode = document.createElement("div");
        this._mountNode.className = "sk-configurator__mount";
        this._mountNode.style.width = "100%";
        this._mountNode.style.height = "100%";
        this._shadowRoot.appendChild(this._mountNode);

        this._root = createRoot(this._mountNode);
      }
      this.render();
      this.adoptAvailableWidth();
    }

    /**
     * Escapes a shrink-to-fit parent by taking an explicit width from the first
     * ancestor the page itself sizes (see `findAvailableWidth`). Opt out with
     * `data-fit-width="off"` if a host page wants the element left alone.
     *
     * Only that one ancestor is observed afterwards, and the width is never
     * re-measured from our own box: re-measuring would collapse the parent back
     * to our content width, grow us again, and loop forever.
     */
    private adoptAvailableWidth() {
      if (this.getAttribute("data-fit-width") === "off") return;

      const available = findAvailableWidth(this);
      if (!available) return;

      const apply = (width: number) => {
        // Never wider than the viewport, or a narrow screen gets a scrollbar.
        const capped = Math.min(width, document.documentElement.clientWidth);
        this.style.width = `${Math.floor(capped)}px`;
      };

      apply(available.width);

      this._widthObserver?.disconnect();
      this._widthObserver = new ResizeObserver(() => {
        apply(innerWidth(available.source));
      });
      this._widthObserver.observe(available.source);
    }

    attributeChangedCallback() {
      this.render();
    }

    disconnectedCallback() {
      this._widthObserver?.disconnect();
      this._widthObserver = null;
      this.style.removeProperty("width");
      // Unmount React to avoid leaks.
      this._root?.unmount();
      this._root = null;
      this._mountNode = null;
      this._shadowRoot = null;
    }

    private render() {
      if (!this._root) return;
      const configId = this.getAttribute("data-config-id") ?? undefined;
      const config = parseJsonAttribute<ConfiguratorConfig>(
        this.getAttribute("data-config"),
      );
      const renderer =
        (this.getAttribute("data-renderer") as RendererKind | null) ??
        undefined;
      const assetsBaseUrl =
        this.getAttribute("data-assets-base-url") ??
        options.defaultAssetsBaseUrl;

      this._root.render(
        createElement(
          ConfiguratorStateProvider,
          { value: configuratorStateAdapter },
          createElement(WidgetRoot, {
            config,
            configId,
            renderer,
            assetsBaseUrl,
          }),
        ),
      );
    }
  }

  customElements.define(tagName, SkConfiguratorElement);
}
